import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { EFeatureToggles, EOrderStatusTypes } from '../../../configurations/common';
import { fromEvent, interval, merge, Observable, Subject, Subscription, takeWhile, timer } from 'rxjs';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import { OrdersFacade } from '../../../facades/orders.facade';
import { EOrderStatus } from '../../../configurations/order-status';
import { IPayments } from '../../../models/order.models';
import { IPaymentsData } from '../../../models/checkout.models';
import { SipayceCardPaymentUtils } from '../../../utils/sipayce-card-payment.utils';

@Component({
  selector: 'app-harmonized-order-status-page',
  templateUrl: './harmonized-order-status-page.component.html',
  styleUrl: './harmonized-order-status-page.component.scss',
  standalone: false,
})
export class HarmonizedOrderStatusPageComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private ordersFacade: OrdersFacade = inject(OrdersFacade);

  protected readonly EOrderStatusTypes: typeof EOrderStatusTypes = EOrderStatusTypes;
  protected readonly window: Window = window;

  private readonly issueWithSapOrderStatuses: string[] = [EOrderStatus.ISSUE, EOrderStatus.ISSUE_P58];

  orderStatus: string = EOrderStatusTypes.PAYMENT_FAILED;
  orderId: string = '';
  guestUserId: string = '';

  isExternalQuoteCheckoutEnabled$: Observable<boolean> =
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.EXTERNAL_QUOTE_CHECKOUT);

  navigationButtonsSubscription!: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();

  orderCallInterval: number = 3000;

  ngOnInit(): void {
    this.checkOrderInfo();
    this._disableNavigationToPreviousRoute();
  }

  /**
   * Disable the navigation to the previous route.
   * This is done by pushing the current route to the history stack and subscribing to the popstate event.
   */
  private _disableNavigationToPreviousRoute(): void {
    history.pushState(null, '', location.href);

    this.navigationButtonsSubscription = fromEvent(window, 'popstate').subscribe(_ => {
      history.pushState(null, '', location.href);
      this.configurationFacade.appendNotification({
        type: 'warning',
        title: 'error.title-you-cannot-to-navigate-back',
        messages: [{
          key: 'error-404.prohibited-action',
        }],
      });
    });
  }

  /**
   * Check if there is a order status state in the query params.
   * Possible states are defined in EOrderStatusTypes.
   * In case, the order status state is not valid, it will be set to EOrderStatusTypes.PAYMENT_FAILED (default value).
   */
  checkOrderInfo(): void {
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      this.orderId = params['orderId'] || '';
      this.guestUserId = params['guestUserId'] || '';

      const failure: EOrderStatusTypes = params['state'];
      if (failure && Object.values(EOrderStatusTypes).includes(failure)) {
        this.orderStatus = failure;
      } else {
        this.orderStatus = EOrderStatusTypes.PAYMENT_FAILED;
        this.router.navigate([], {
          queryParams: {state: this.orderStatus},
          queryParamsHandling: 'merge', // Keeps other existing query params
        });
      }
    });

    //in case of pending payment (Sipayce), call the orders endpoint periodically
    if (this.orderStatus === EOrderStatusTypes.PAYMENT_PENDING) {
      if (!this.orderId) {
        this.configurationFacade.appendNotification({
          type: 'warning',
          title: 'error.we-cannot-continue-without-order-id',
          messages: [{
            key: 'error-404.no-id-provided',
          }],
        });
      } else {
        this._getOrderEndpointCallingInterval();
        this._callOrderEndpointPeriodicallyForPaymentUpdate();
      }
      return;
    }

    //in case of pending order (SAP), call the orders endpoint periodically
    if (this.orderStatus === EOrderStatusTypes.ORDER_PENDING) {
      if (!this.orderId) {
        this.configurationFacade.appendNotification({
          type: 'warning',
          title: 'error.we-cannot-continue-without-order-id',
          messages: [{
            key: 'error-404.no-id-provided',
          }],
        });
      } else {
        this._getOrderEndpointCallingInterval();
        this._callOrderEndpointPeriodicallyForSapUpdate();
      }
    }
  }

  /**
   * Call orders endpoint periodically to check if the payment is completed (successful, failed or aborted).
   *
   * @private
   */
  private _callOrderEndpointPeriodicallyForPaymentUpdate(): void {
    const timer$: Observable<number> = timer(15 * 60 * 1000);

    interval(this.orderCallInterval).pipe(
      switchMap(() => this.ordersFacade.getOrderById(this.orderId, this.guestUserId)),
      takeWhile(response => response.data.attributes.stateDisplayName === EOrderStatus.PAYMENT_PENDING, true),
      takeUntil(merge(timer$, this.unsubscribe$)),
    ).subscribe(response => {
      const payments: IPaymentsData | IPayments[] | undefined = response.data.attributes.payments;
      const paymentStatus: string | undefined = Array.isArray(payments) ? payments[0]?.orderStatus : undefined;

      if (SipayceCardPaymentUtils.isPaymentSuccessful(paymentStatus)) {
        this.ordersFacade.recallAndClearOrdersWithDetailsDataList();
        this.router.navigate(['/order-thank-you-page', this.guestUserId, this.orderId]);
        return;
      }

      if (SipayceCardPaymentUtils.isPaymentFailed(paymentStatus)) {
        this.orderStatus = EOrderStatusTypes.PAYMENT_FAILED;
        this.router.navigate([], {
          queryParams: {state: this.orderStatus},
          queryParamsHandling: 'merge',
        });
        return;
      }

      if (SipayceCardPaymentUtils.isPaymentAborted(paymentStatus)) {
        this.orderStatus = EOrderStatusTypes.PAYMENT_ABORTED;
        this.router.navigate([], {
          queryParams: {state: this.orderStatus},
          queryParamsHandling: 'merge',
        });
        return;
      }
    });

    timer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.orderStatus = EOrderStatusTypes.PAYMENT_FAILED;
        this.router.navigate([], {
          queryParams: {state: this.orderStatus},
          queryParamsHandling: 'merge',
        });
      });
  }

  /**
   * Call orders endpoint periodically to check if the SAP order is completed.
   */
  private _callOrderEndpointPeriodicallyForSapUpdate(): void {
    const timer$: Observable<number> = timer(3 * 60 * 1000);

    interval(this.orderCallInterval).pipe(
      switchMap(() => this.ordersFacade.getOrderById(this.orderId)),
      takeWhile(
        response =>
          response.data.attributes.sapId === null &&
          !this.issueWithSapOrderStatuses.includes(response.data.attributes.stateDisplayName ?? ''),
        true,
      ),
      takeUntil(merge(timer$, this.unsubscribe$)),
    ).subscribe(response => {
      if (response.data.attributes.sapId !== null) {
        this.ordersFacade.recallAndClearOrdersWithDetailsDataList();
        this.router.navigate(['/order-thank-you-page/', response.data.id]);
      }
      if (this.issueWithSapOrderStatuses.includes(response.data.attributes.stateDisplayName ?? '')) {
        this.orderStatus = EOrderStatusTypes.ORDER_FINALIZATION_ERROR;
        this.router.navigate([], {
          queryParams: {state: this.orderStatus},
          queryParamsHandling: 'merge',
        });
        return;
      }
    });

    timer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
      this.orderStatus = EOrderStatusTypes.ORDER_FINALIZATION_ERROR;
      this.router.navigate([], {
        queryParams: {state: this.orderStatus},
        queryParamsHandling: 'merge',
      });
    });
  }

  /**
   * Get the interval for calling the orders endpoint.
   */
  private _getOrderEndpointCallingInterval(): void {
    this.configurationFacade.getPendingPageGettingOrdersInterval()
      .pipe(take(1))
      .subscribe(interval => this.orderCallInterval = interval);
  }

  ngOnDestroy(): void {
    this.navigationButtonsSubscription?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
